/**
 * Format a number as currency (Euro)
 */
export function formatCurrency(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) {
    return '-'
  }
  return `€${value.toFixed(decimals)}`
}

/**
 * Format a price range for a collapsed master (one canonical row spanning
 * several pressings). Falls back to a single value when the range is a point,
 * when only one bound is known, or when no range is present (non-collapsed
 * rows). `decimals = 0` keeps range chips compact.
 */
export function formatPriceRange(
  low: number | null | undefined,
  high: number | null | undefined,
  single: number | null | undefined,
): string {
  const lo = low ?? null
  const hi = high ?? null
  // A genuine range → compact whole-euro bounds ("€30–95").
  if (lo !== null && hi !== null && Math.round(lo) !== Math.round(hi)) {
    return `€${lo.toFixed(0)}–${hi.toFixed(0)}`
  }
  // A single price (point range or non-collapsed row) → keep cents.
  const one = hi ?? lo ?? single ?? null
  return formatCurrency(one)
}

/**
 * Format a number as a percentage
 */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined) {
    return '-'
  }
  return `${value.toFixed(decimals)}%`
}

/**
 * Format a number with sign (+ or -)
 */
export function formatWithSign(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) {
    return '-'
  }
  const sign = value >= 0 ? '+' : ''
  return `${sign}€${value.toFixed(decimals)}`
}

/**
 * Format a date string (ISO format to readable format)
 */
export function formatDate(date: string | null | undefined): string {
  if (!date) {
    return '-'
  }
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format a number with thousands separator
 */
export function formatNumber(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined) {
    return '-'
  }
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}
